import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Mail, Phone, Globe, ArrowRight, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * "Change booking" — booking changes are the operator's call.
 * We surface their full contact card so the customer connects directly.
 */
export const ChangeBookingModal = ({ booking, open, onClose }) => {
  if (!booking) return null;
  const org = booking.organization || {};

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="change-booking-modal">
        <DialogHeader>
          <DialogTitle>Change or cancel this booking</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-amber-50 rounded-xl p-3.5 flex items-start gap-2.5">
            <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              Date changes, reschedules and cancellations are decided by the operator — BookingFlows can't change bookings on their behalf. Reach out to them directly and they'll take care of you.
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4" data-testid="change-booking-operator-card">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Your operator</p>
            <p className="text-base font-bold text-gray-900">{org.name}</p>
            <p className="text-xs text-gray-500 mb-3">{booking.service_name} · {booking.confirmation_code || ''}</p>

            <div className="space-y-2.5">
              {org.phone && (
                <a href={`tel:${org.phone}`} className="flex items-center gap-2.5 text-sm text-gray-800 hover:text-blue-600 transition-colors" data-testid="change-booking-phone">
                  <Phone className="w-4 h-4 text-gray-400" /> {org.phone}
                </a>
              )}
              {org.email && (
                <a href={`mailto:${org.email}?subject=Booking change request${booking.confirmation_code ? ` — ${booking.confirmation_code}` : ''}`} className="flex items-center gap-2.5 text-sm text-gray-800 hover:text-blue-600 transition-colors break-all" data-testid="change-booking-email">
                  <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" /> {org.email}
                </a>
              )}
              {org.website && (
                <a href={org.website.startsWith('http') ? org.website : `https://${org.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-sm text-gray-800 hover:text-blue-600 transition-colors break-all" data-testid="change-booking-website">
                  <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" /> {org.website.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center">
            Their contact details are also on your ticket.{' '}
            <Link to="/support" onClick={onClose} className="inline-flex items-center gap-1 text-blue-600 font-medium hover:text-blue-700">
              More help <ArrowRight className="w-3 h-3" />
            </Link>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeBookingModal;
